import { useState } from "react";
import type { QuoteDetailDto } from "@/lib/quotes";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";

const fmt = new Intl.NumberFormat("en-AU", { style: "decimal", minimumFractionDigits: 2, maximumFractionDigits: 2 });
function formatCurrency(v: number | null | undefined): string {
  return v == null ? "—" : fmt.format(v);
}

// Mirrors the server derivation (QuoteService.SetManualTotalsAsync): GST locked
// at 10%, rounded away from zero to 2dp. Done in integer cents so the live
// preview matches the server's exact decimal rounding for ALL inputs — a naive
// `round(ex * 0.10)` on the raw float diverges (e.g. 1.45 → 0.145 stored as
// 0.14499… → wrong $0.14 instead of the server's $0.15).
export function deriveGst(ex: number): { gst: number; inc: number } {
  const exCents = Math.round(ex * 100);
  const gstCents = Math.round(exCents * 0.1); // 10% of ex in cents; half-up == away-from-zero on the non-negative domain
  return { gst: gstCents / 100, inc: (exCents + gstCents) / 100 };
}

export interface QuoteCostsCardProps {
  quote: QuoteDetailDto;
  editable?: boolean;
  onSetManualTotals?: (totalExGst: number | null) => void;
}

/**
 * Right-column-top Costs card. Subtotal / GST / Total inc GST.
 * Backlog #9 — when editable, lets the user override the subtotal (ex-GST)
 * directly; GST/total are derived client-side for preview, matching the
 * server's SetManualTotalsAsync derivation.
 */
export function QuoteCostsCard({ quote, editable, onSetManualTotals }: QuoteCostsCardProps) {
  const [editing, setEditing] = useState(false);
  const [exText, setExText] = useState("");


  const parsed = Number.parseFloat(exText);
  // Reject non-numbers, negatives, out-of-range (server column max), and >2dp so
  // the user sees a disabled Save instead of a server 400 + generic toast.
  const valid =
    Number.isFinite(parsed) &&
    parsed >= 0 &&
    parsed <= 99_999_999.99 &&
    Number(parsed.toFixed(2)) === parsed;
  const preview = valid ? deriveGst(parsed) : null;

  return (
    <Card className="border border-slate-400 rounded-xl overflow-hidden bg-white shadow-sm">
      <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-3 px-4">
        <div className="flex items-center justify-between">
          <CardTitle
            role="heading"
            aria-level={3}
            className="text-xs font-bold text-slate-800 uppercase tracking-wider"
          >
            Costs
          </CardTitle>
          {quote.totalsManuallySet && <Badge variant="secondary">Manual</Badge>}
        </div>
      </CardHeader>
      <CardContent className="space-y-3 p-4">
        {!editing && (
          <dl className="space-y-4 pt-2">
            <div className="flex justify-between items-center text-sm">
              <dt className="font-bold text-[#1a2f4c]">Subtotal</dt>
              <dd className="font-bold text-[#1a2f4c] flex items-center gap-2">
                {formatCurrency(quote.totalExGst)}
                <button
                  type="button"
                  className="text-slate-400 hover:text-red-600 transition-colors cursor-pointer ml-1"
                  aria-label="Remove subtotal"
                >
                  <X className="w-4 h-4" strokeWidth={2.5} />
                </button>
              </dd>
            </div>
            <div className="flex justify-between items-center text-sm">
              <dt className="text-slate-500">Delivery</dt>
              <dd className="text-slate-500 flex items-center gap-2">
                {formatCurrency(0)}
                <button
                  type="button"
                  className="text-slate-400 hover:text-red-600 transition-colors cursor-pointer ml-1"
                  aria-label="Remove delivery"
                >
                  <X className="w-4 h-4" strokeWidth={2.5} />
                </button>
              </dd>
            </div>
            <div className="flex justify-between items-center text-sm">
              <dt className="text-slate-500">Tax</dt>
              <dd className="text-slate-500 flex items-center gap-2">
                {formatCurrency(quote.gst)}
                <button
                  type="button"
                  className="text-slate-400 hover:text-red-600 transition-colors cursor-pointer ml-1"
                  aria-label="Remove tax"
                >
                  <X className="w-4 h-4" strokeWidth={2.5} />
                </button>
              </dd>
            </div>
            <div className="flex justify-between items-center text-sm pt-4">
              <dt className="font-bold text-[#1a2f4c]">Total</dt>
              <dd className="font-bold text-[#1a2f4c] flex items-center gap-2">
                {formatCurrency(quote.totalIncGst)}
                <button
                  type="button"
                  className="text-slate-400 hover:text-red-600 transition-colors cursor-pointer ml-1"
                  aria-label="Remove total"
                >
                  <X className="w-4 h-4" strokeWidth={2.5} />
                </button>
              </dd>
            </div>
          </dl>
        )}

        {editing && (
          <div className="space-y-2">
            <Label htmlFor="manual-ex-gst">Subtotal (ex-GST)</Label>
            <Input
              id="manual-ex-gst"
              inputMode="decimal"
              value={exText}
              onChange={(e) => setExText(e.target.value)}
              placeholder="0.00"
            />
            <div className="text-sm text-muted-foreground">
              GST (10%): <span>{preview ? fmt.format(preview.gst) : "—"}</span>
              {" · "}Total (inc-GST): <span>{preview ? fmt.format(preview.inc) : "—"}</span>
            </div>
            <div className="flex gap-2">
              <Button size="sm" disabled={!valid} onClick={() => { onSetManualTotals?.(parsed); setEditing(false); }}>
                Save
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {editable && !editing && (
          <div className="flex gap-2">
            <Button
              size="sm" variant="outline"
              onClick={() => { setExText(quote.totalExGst != null ? String(quote.totalExGst) : ""); setEditing(true); }}
            >
              Edit totals
            </Button>
            {quote.totalsManuallySet && (
              <Button size="sm" variant="ghost" onClick={() => onSetManualTotals?.(null)}>
                Revert to calculated
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
